import Component from "@glimmer/component";
import { arg, forbidExtraArgs } from "glesys-controlpanel/utils/arg";
import { service } from "@ember/service";
import { oneOf } from "prop-types";
import { stringable } from "glesys-controlpanel/utils/prop-types";

import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import IconsWarning from "glesys-controlpanel/components/icons/warning";
import IconsError from "glesys-controlpanel/components/icons/error";
import IconsCheckCircle from "glesys-controlpanel/components/icons/check-circle";
import IconsInfo from "glesys-controlpanel/components/icons/info";
import { cached } from "@glimmer/tracking";

@forbidExtraArgs
export default class AlertMessage extends Component {
  @service intl;

  @arg(stringable) message;

  @arg(oneOf(["confirmation", "information", "warning", "error"])) type;

  @cached
  get config() {
    return (
      {
        confirmation: {
          classes: "bg-green-100/10 text-green-700 border rounded-sm border-green-100/40",
          label: this.intl.t("alert.confirmation"),
        },
        information: {
          classes: "bg-indigo-100 text-black border rounded-sm border-indigo-100",
          label: this.intl.t("alert.information"),
        },
        warning: {
          classes: "bg-orange-100 text-orange-800 border rounded-sm border-orange-200",
          label: this.intl.t("alert.warning"),
        },
        error: {
          classes: "bg-red-100 text-red-800 border rounded-sm border-red-200",
          label: this.intl.t("alert.error"),
        },
      }[this.type] ?? {
        classes: "bg-indigo-100 text-black border rounded-sm border-indigo-100",
        label: this.intl.t("alert.information"),
      }
    );
  }

  get classes() {
    return this.config.classes;
  }

  get label() {
    return this.config.label;
  }

  get isWarning() {
    return this.type === "warning";
  }

  get isError() {
    return this.type === "error";
  }

  get isConfirmation() {
    return this.type === "confirmation";
  }

  <template>
    <div class="{{this.classes}} flex shadow-base text-sm px-4 py-3" role="alert" {{mergeClasses}} ...attributes>
      <div class="flex items-center mr-3">
        {{#if this.isWarning}}
          <IconsWarning class="stroke-current size-4" />
        {{else if this.isError}}
          <IconsError class="stroke-current size-4" />
        {{else if this.isConfirmation}}
          <IconsCheckCircle class="stroke-current stroke-1 size-5" />
        {{else}}
          <IconsInfo class="stroke-current size-4" />
        {{/if}}
      </div>
      <span class="sr-only">{{this.label}}</span>
      {{#if this.message}}
        <div data-test-alert-message={{this.type}}>
          <p>
            {{this.message}}
          </p>
        </div>
      {{else}}
        {{#if (has-block)}}
          {{yield}}
        {{/if}}
      {{/if}}
    </div>
  </template>
}
